import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, Edit, Trash2, Package, CheckCircle, ChevronLeft, ChevronRight, RefreshCw, Clock, User, Calendar, Search, Filter, AlertTriangle, SortAsc, SortDesc } from 'lucide-react';
import LoanForm from '../components/LoanForm';
import { useNavigate } from 'react-router-dom';
import { loansService } from '../services/loansService';
import { productsService } from '../services/productsService';

const LoansPage = () => {
  const [loans, setLoans] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    returned: 0,
    overdue: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    material: '',
    borrower: '',
    status: '',
    date: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [itemsPerPage] = useState(20);
  // Cargar datos desde el servidor
  useEffect(() => {
    loadData();
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    // Actualizar conteo de vencidos cuando cambian los préstamos
    if (loans.length > 0) {
      const overdue = loans.filter(loan => !loan.returned && isOverdue(loan.expected_return_date, loan.returned)).length;
      setStats(prev => ({ ...prev, overdue }));
    }
  }, [loans]);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Cargando datos de préstamos y productos...');
      
      const [loansResponse, productsResponse, statsResponse] = await Promise.all([
        loansService.getAll(),
        productsService.getAllUnpaginated(),
        loansService.getStats().catch(() => null)
      ]);

      console.log('📦 Respuesta de productos:', productsResponse);
      console.log('📦 Tipo de respuesta:', typeof productsResponse);
      console.log('📦 Es array?:', Array.isArray(productsResponse));

      if (loansResponse && loansResponse.success) {
        console.log(`✅ Datos de préstamos cargados: ${loansResponse.data.length} registros`);
        setLoans(loansResponse.data);
      }
      
      if (productsResponse && productsResponse.success) {
        console.log(`✅ Datos de productos cargados: ${productsResponse.data.length} productos`);
        setProducts(productsResponse.data);
      } else if (Array.isArray(productsResponse)) {
        console.log(`✅ Datos de productos (array directo): ${productsResponse.length} productos`);
        setProducts(productsResponse);
      } else {
        console.warn('⚠️ Formato de respuesta de productos no reconocido:', productsResponse);
      }
      
      if (statsResponse && statsResponse.success) {
        console.log('✅ Estadísticas cargadas');
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('❌ Error loading data:', error);
      alert('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLoan = () => {
    setEditingLoan(null);
    setShowForm(true);
  };

  const handleEditLoan = (loan) => {
    setEditingLoan(loan);
    setShowForm(true);
  };

  const handleDeleteLoan = async (id) => {
    const loan = loans.find(l => l.id === id);
    if (!loan) return;

    const message = loan.returned
      ? '¿Estás seguro de que quieres eliminar este registro de préstamo?'
      : '¿Estás seguro de que quieres eliminar este registro de préstamo? Si la pieza no ha sido devuelta, se ajustará automáticamente el inventario.';

    if (window.confirm(message)) {
      try {
        const response = await loansService.delete(id);
        if (response.success) {
          await loadData();
          alert('✅ Préstamo eliminado');
        }
      } catch (error) {
        console.error('Error deleting loan:', error);
        alert('❌ Error al eliminar el préstamo');
      }
    }
  };
  const handleSaveLoan = async (loanData) => {
    try {
      let response;
      if (editingLoan) {
        response = await loansService.update(editingLoan.id, loanData);
      } else {
        response = await loansService.create(loanData);
      }

      if (response.success) {
        await loadData(); // Recargar datos
        setShowForm(false);
        setEditingLoan(null);
        alert(`✅ Préstamo ${editingLoan ? 'actualizado' : 'creado'} exitosamente`);
      }
    } catch (error) {
      console.error('Error saving loan:', error);
      if (error.response?.status === 400) {
        alert(`❌ ${error.response.data.message}`);
      } else {
        alert('❌ Error al guardar el préstamo');
      }
    }
  };

  const handleReturnLoan = async (id) => {
    const loan = loans.find(l => l.id === id);
    if (!loan || loan.returned) return;

    const productName = getProductName(loan.product_id);
    
    if (window.confirm(`¿Confirmas la devolución de ${loan.quantity} unidades de ${productName}?`)) {
      try {
        const response = await loansService.returnLoan(id);
        if (response.success) {
          await loadData(); // Recargar datos
          alert(`✅ ${loan.quantity} unidades de ${productName} devueltas al inventario.`);
        }
      } catch (error) {
        console.error('Error returning loan:', error);
        alert('❌ Error al procesar la devolución');
      }
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingLoan(null);
  };

  const getProductName = (productId) => {
    // Buscar primero por ID numérico
    let product = products.find(p => p.id === productId);
    
    // Si no se encuentra por ID, intentar convertir el ID a número
    if (!product && typeof productId === 'string') {
      const numId = parseInt(productId, 10);
      if (!isNaN(numId)) {
        product = products.find(p => p.id === numId);
      }
    }
    
    // Si aún no se encuentra, buscar por product_name y product_sku que podrían venir del backend
    if (!product && loans.length > 0) {
      const loan = loans.find(l => l.product_id === productId);
      if (loan && loan.product_name) {
        return `${loan.product_name} (${loan.product_sku || 'Sin SKU'})`;
      }
    }
    
    // Devolver el formato estándar si se encontró el producto, o mensaje de error
    return product ? `${product.name} (${product.sku || 'Sin SKU'})` : 'Producto no encontrado';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() + userTimezoneOffset);
    return localDate.toLocaleDateString('es-MX');
  };

  const isOverdue = (expectedReturnDate, returned) => {
    if (returned) return false;
    const today = new Date();
    const returnDate = new Date(expectedReturnDate);
    return returnDate < today;
  };

  // Lógica de paginación y filtrado
  const filteredLoans = loans.filter(loan => {
    // Usar el nombre del producto ya sea desde la función getProductName o directamente del loan si existe
    const productNameFromFunc = getProductName(loan.product_id).toLowerCase();
    const productNameDirect = loan.product_name ? loan.product_name.toLowerCase() : '';
    const productName = productNameFromFunc.includes('no encontrado') ? productNameDirect : productNameFromFunc;
    
    // Asegurarse de que borrower_name existe antes de llamar a toLowerCase()
    const borrowerName = loan.borrower_name ? loan.borrower_name.toLowerCase() : '';
    const searchTermLower = searchTerm.toLowerCase();
    
    return (
      productName.includes(searchTermLower) ||
      borrowerName.includes(searchTermLower) ||
      (loan.borrower_contact && loan.borrower_contact.toLowerCase().includes(searchTermLower)) ||
      (loan.purpose && loan.purpose.toLowerCase().includes(searchTermLower)) ||
      (loan.requisition && loan.requisition.toLowerCase().includes(searchTermLower))
    );
  });

  // Aplicar filtros si están configurados
  const applyFilters = (loans) => {
    return loans.filter(loan => {
      let pass = true;
      
      if (filters.material && !getProductName(loan.product_id).toLowerCase().includes(filters.material.toLowerCase())) {
        pass = false;
      }
      
      if (filters.borrower && !loan.borrower_name.toLowerCase().includes(filters.borrower.toLowerCase())) {
        pass = false;
      }
      
      if (filters.status) {
        if (filters.status === 'returned' && !loan.returned) pass = false;
        if (filters.status === 'pending' && loan.returned) pass = false;
        if (filters.status === 'overdue' && (!isOverdue(loan.expected_return_date, loan.returned) || loan.returned)) pass = false;
      }
      
      return pass;
    });
  };

  // Aplicar ordenamiento si está configurado
  const sortedLoans = sortConfig.key 
    ? [...filteredLoans].sort((a, b) => {
        let aValue, bValue;
        
        // Determinar los valores a comparar según la clave de ordenamiento
        switch (sortConfig.key) {
          case 'material':
            aValue = getProductName(a.product_id);
            bValue = getProductName(b.product_id);
            break;
          case 'quantity':
            aValue = a.quantity;
            bValue = b.quantity;
            break;
          case 'borrower':
            aValue = a.borrower_name;
            bValue = b.borrower_name;
            break;
          case 'loan_date':
            aValue = new Date(a.loan_date);
            bValue = new Date(b.loan_date);
            break;
          case 'expected_return_date':
            aValue = new Date(a.expected_return_date);
            bValue = new Date(b.expected_return_date);
            break;
          case 'status':
            aValue = a.returned ? 2 : isOverdue(a.expected_return_date, a.returned) ? 0 : 1;
            bValue = b.returned ? 2 : isOverdue(b.expected_return_date, b.returned) ? 0 : 1;
            break;
          default:
            return 0;
        }
        
        // Aplicar dirección de ordenamiento
        const comparison = typeof aValue === 'string' 
          ? aValue.localeCompare(bValue) 
          : aValue - bValue;
          
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      })
    : filteredLoans;

  const finalLoans = applyFilters(sortedLoans);
  const totalPages = Math.ceil(finalLoans.length / itemsPerPage);
  const currentLoans = finalLoans.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleRequestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Resetear a la página 1 cuando cambian los filtros o la búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, sortConfig]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-180px)] px-4 py-8 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Cargando préstamos...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-[calc(100vh-180px)] px-4 py-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <h2 className="text-4xl font-extrabold text-gray-800 mb-8 text-center bg-gradient-to-r from-indigo-600 to-teal-600 bg-clip-text text-transparent">
        Préstamo de Piezas
      </h2>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Préstamos</p>
              <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
            </div>
            <Package className="w-8 h-8 text-blue-600" />
          </div>
        </motion.div>

        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Pendientes</p>
              <p className="text-3xl font-bold text-orange-600">{stats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-orange-600" />
          </div>
        </motion.div>

        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Devueltos</p>
              <p className="text-3xl font-bold text-green-600">{stats.returned}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </motion.div>

        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Vencidos</p>
              <p className="text-3xl font-bold text-red-600">{stats.overdue}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
        </motion.div>
      </div>

      <div className="flex justify-between items-center gap-4 mb-8">
        <motion.button
          onClick={handleAddLoan}
          className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          <PlusCircle className="w-5 h-5" />
          Nuevo Préstamo
        </motion.button>

        <div className="flex-1 flex items-center max-w-md relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Buscar por material, prestatario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <motion.button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </motion.button>
      </div>

      <AnimatePresence mode="wait">
        {showForm && (
          <LoanForm
            currentLoan={editingLoan}
            onSave={handleSaveLoan}
            onCancel={handleCancelForm}
            products={products}
          />
        )}
      </AnimatePresence>

      {!showForm && (
        <motion.div
          className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-full mx-auto border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {loans.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-20 h-20 mx-auto mb-4 text-gray-300" />
              <p className="text-xl font-medium">No hay préstamos registrados. ¡Todo está en su sitio!</p>
            </div>
          ) : finalLoans.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Search className="w-20 h-20 mx-auto mb-4 text-gray-300" />
              <p className="text-xl font-medium">No se encontraron préstamos con los filtros aplicados.</p>
              <button 
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                onClick={() => {
                  setSearchTerm('');
                  setFilters({
                    material: '',
                    borrower: '',
                    status: '',
                    date: ''
                  });
                }}
              >
                Limpiar filtros
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center space-x-1 cursor-pointer">
                          <span>ID</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center space-x-1 cursor-pointer" onClick={() => handleRequestSort('material')}>
                          <span>Material</span>
                          {sortConfig.key === 'material' && (
                            sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                          )}
                        </div>
                        <div className="mt-2">
                          <input
                            type="text"
                            placeholder="Filtrar material"
                            className="w-full p-1 text-xs border border-gray-300 rounded"
                            value={filters.material}
                            onChange={(e) => setFilters({...filters, material: e.target.value})}
                          />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center space-x-1 cursor-pointer" onClick={() => handleRequestSort('quantity')}>
                          <span>Cantidad</span>
                          {sortConfig.key === 'quantity' && (
                            sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center space-x-1 cursor-pointer" onClick={() => handleRequestSort('borrower')}>
                          <span>Prestatario</span>
                          {sortConfig.key === 'borrower' && (
                            sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                          )}
                        </div>
                        <div className="mt-2">
                          <input
                            type="text"
                            placeholder="Filtrar prestatario"
                            className="w-full p-1 text-xs border border-gray-300 rounded"
                            value={filters.borrower}
                            onChange={(e) => setFilters({...filters, borrower: e.target.value})}
                          />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taller / Dpto</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requisición</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center space-x-1 cursor-pointer" onClick={() => handleRequestSort('loan_date')}>
                          <span>Fecha Préstamo</span>
                          {sortConfig.key === 'loan_date' && (
                            sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center space-x-1 cursor-pointer" onClick={() => handleRequestSort('expected_return_date')}>
                          <span>Fecha Devolución Esperada</span>
                          {sortConfig.key === 'expected_return_date' && (
                            sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center space-x-1 cursor-pointer" onClick={() => handleRequestSort('status')}>
                          <span>Estado</span>
                          {sortConfig.key === 'status' && (
                            sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                          )}
                        </div>
                        <div className="mt-2">
                          <select
                            className="w-full p-1 text-xs border border-gray-300 rounded"
                            value={filters.status}
                            onChange={(e) => setFilters({...filters, status: e.target.value})}
                          >
                            <option value="">Todos</option>
                            <option value="pending">Pendientes</option>
                            <option value="returned">Devueltos</option>
                            <option value="overdue">Vencidos</option>
                          </select>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <AnimatePresence>
                      {currentLoans.map((loan) => (
                        <motion.tr
                          key={loan.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.3 }}
                          className={`hover:bg-gray-50 ${
                            isOverdue(loan.expected_return_date, loan.returned) ? 'bg-red-50' : ''
                          }`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm font-medium text-gray-900">{loan.id}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" onClick={() => navigate(`/loans/${loan.id}`)} style={{cursor: 'pointer'}}>
                            <div className="text-sm font-medium text-gray-900">
                              {/* Usar product_name directamente del loan si está disponible, o caer en getProductName */}
                              {loan.product_name ? `${loan.product_name} (${loan.product_sku || 'Sin SKU'})` : getProductName(loan.product_id)}
                            </div>
                            {loan.purpose && (
                              <div className="text-xs text-gray-500">{loan.purpose}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{loan.quantity}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{loan.borrower_name || 'Sin nombre'}</div>
                            {loan.borrower_contact && (
                              <div className="text-xs text-gray-500">{loan.borrower_contact}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-700">{loan.workshop || loan.department || loan.dept || loan.taller || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-700">{loan.requisition || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(loan.loan_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={isOverdue(loan.expected_return_date, loan.returned) ? 'text-red-600 font-medium' : ''}>
                              {formatDate(loan.expected_return_date)}
                            </span>
                            {isOverdue(loan.expected_return_date, loan.returned) && (
                              <div className="text-xs text-red-500">¡Vencido!</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              loan.returned 
                                ? 'bg-green-100 text-green-800' 
                                : isOverdue(loan.expected_return_date, loan.returned)
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {loan.returned 
                                ? `Devuelto (${formatDate(loan.return_date)})` 
                                : isOverdue(loan.expected_return_date, loan.returned)
                                  ? 'Vencido'
                                  : 'Pendiente'
                              }
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center space-x-3">
                              {!loan.returned && (
                                <motion.button
                                  onClick={() => handleReturnLoan(loan.id)}
                                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors duration-200"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Devolver
                                </motion.button>
                              )}
                              <motion.button
                                onClick={() => handleEditLoan(loan)}
                                className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <Edit className="w-4 h-4" />
                                Editar
                              </motion.button>
                              <motion.button
                                onClick={() => handleDeleteLoan(loan.id)}
                                className="text-red-600 hover:text-red-900 flex items-center gap-1"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <Trash2 className="w-4 h-4" />
                                Borrar
                              </motion.button>
                            </div>
                          </td>
                            
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                  <motion.button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </motion.button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <motion.button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-4 py-2 rounded-full font-medium ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {page}
                    </motion.button>
                  ))}
                  <motion.button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </motion.button>
                </div>
              )}
            </>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default LoansPage;